import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import RatingModal from './RatingModal';
import { useConfirmDialog } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { clearAuthSession, ensureAuthenticated } from '../services/authGuardService';
import { getApiErrorMessage, readJsonSafe } from '../services/apiResponseService';
import styles from '../styles/modules/MyEngagements.module.css';
import headerStyles from '../styles/modules/Header.module.css';

interface Engagement {
    _id: string;
    jobRole: string;
    date: string;
    startTime: string;
    endTime: string;
    place: string;
    facilityName?: string;
    payRate?: string;
    status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
    startedBy: 'employer' | 'bartender';
    completedBy?: 'employer' | 'bartender' | 'both';
    completedDate?: string;
    createdAt: string;
    updatedAt: string;
    // Other party info
    otherParty: {
        _id: string;
        fullName: string;
        businessName?: string;
        email: string;
        phone?: string;
        userType: string;
    };
    description?: string;
    notes?: string;
}

const MyEngagements: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { confirm } = useConfirmDialog();
    const { showToast } = useToast();
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
    const [engagements, setEngagements] = useState<Engagement[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [ratingModalOpen, setRatingModalOpen] = useState(false);
    const [selectedEngagement, setSelectedEngagement] = useState<Engagement | null>(null);
    const [ratedEngagements, setRatedEngagements] = useState<Set<string>>(new Set());
    
    // Get current user from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isEmployer = user.role === 'employer' || user.role === 'venue_owner' || user.userType === 'employer' || user.userType === 'venue_owner';
    
    console.log('User object:', user);
    console.log('isEmployer:', isEmployer);

    const fetchEngagements = async () => {
        const auth = ensureAuthenticated();

        if (!auth.allowed) {
            showToast({ message: t('myEngagements.loginRequiredView'), type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;
        const currentUser = auth.user;

        setLoading(true);
        setError('');

        try {
            const isEmployerRole = currentUser.role === 'employer' || currentUser.role === 'venue_owner';
            
            // Add query parameter based on user role
            const queryParam = isEmployerRole 
                ? `?employerId=${currentUser.id}` 
                : `?bartenderId=${currentUser.id}`;
            
            const response = await fetch(getApiUrl(`engagements/my${queryParam}`), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    clearAuthSession();
                    navigate('/signin');
                    return;
                }
                throw new Error(`Failed to fetch engagements: ${response.statusText}`);
            }

            const data = await response.json();
            setEngagements(data);
        } catch (error) {
            console.error('Error fetching engagements:', error);
            setError(error instanceof Error ? error.message : 'Failed to fetch engagements');
        } finally {
            setLoading(false);
        }
    };

    const checkRatedEngagements = async () => {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        try {
            const response = await fetch(getApiUrl('ratings/my-given?limit=100'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const ratedIds = new Set<string>(
                    data.ratings?.map((r: any) => r.engagement?._id || r.engagementId).filter(Boolean) || []
                );
                setRatedEngagements(ratedIds);
            } else {
                // Silently fail - rating check is optional
                // User can still rate, the POST will tell them if they already rated
                console.log('Could not fetch rating history');
            }
        } catch (error) {
            // Silently fail - rating check is optional
            console.log('Could not fetch rating history');
        }
    };

    const handleOpenRating = (engagement: Engagement) => {
        setSelectedEngagement(engagement);
        setRatingModalOpen(true);
    };

    const handleRatingSubmitted = () => {
        if (selectedEngagement) {
            // Mark this engagement as rated immediately
            setRatedEngagements(prev => {
                const newSet = new Set(prev);
                newSet.add(selectedEngagement._id);
                return newSet;
            });
        }
    };

    useEffect(() => {
        fetchEngagements().then(() => {
            // Only check rated engagements after we have the engagements data
            checkRatedEngagements();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canRateEngagement = (engagement: Engagement) => {
        if (engagement.status !== 'completed' || engagement.completedBy !== 'both') {
            return false;
        }
        
        if (ratedEngagements.has(engagement._id)) {
            return false;
        }

        // Check if within 30-day window
        if (engagement.completedDate) {
            const completedDate = new Date(engagement.completedDate);
            const daysSinceCompletion = Math.floor((Date.now() - completedDate.getTime()) / (1000 * 60 * 60 * 24));
            return daysSinceCompletion <= 30;
        }

        return true;
    };

    const handleMarkComplete = async (engagementId: string) => {
        const auth = ensureAuthenticated();
        if (!auth.allowed) {
            showToast({ message: t('myEngagements.loginRequired'), type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;

        try {
            const response = await fetch(getApiUrl(`engagements/${engagementId}/complete`), {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await readJsonSafe<any>(response);
                throw new Error(getApiErrorMessage(response, t('myEngagements.completeFailed') || 'Failed to mark engagement as complete', errorData));
            }

            const data = await response.json();
            
            if (data.completedBy === 'both') {
                showToast({ message: t('myEngagements.completeBothConfirmed'), type: 'success' });
            } else {
                showToast({ message: t('myEngagements.completeAwaitingConfirm'), type: 'info' });
            }

            // Refresh engagements list
            fetchEngagements();
        } catch (error) {
            console.error('Error marking engagement complete:', error);
            let errorMessage = error instanceof Error ? error.message : t('myEngagements.completeFailed');
            
            // Check for specific error message and use translation
            if (errorMessage.includes('You have already marked this as complete')) {
                errorMessage = t('myEngagements.alreadyMarkedComplete');
            }
            
            showToast({ message: errorMessage, type: 'error' });
        }
    };

    const handleConfirmEngagement = async (engagementId: string) => {
        const auth = ensureAuthenticated();
        if (!auth.allowed) {
            showToast({ message: t('myEngagements.loginRequired'), type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;

        try {
            const response = await fetch(getApiUrl(`engagements/${engagementId}/confirm`), {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await readJsonSafe<any>(response);
                throw new Error(getApiErrorMessage(response, t('myEngagements.confirmFailed') || 'Failed to confirm engagement', errorData));
            }

            showToast({ message: t('myEngagements.confirmSuccess'), type: 'success' });
            
            // Refresh engagements list
            fetchEngagements();
        } catch (error) {
            console.error('Error confirming engagement:', error);
            showToast({ message: error instanceof Error ? error.message : t('myEngagements.confirmFailed'), type: 'error' });
        }
    };

    const handleCancelEngagement = async (engagementId: string) => {
        const confirmed = await confirm({
            title: t('myEngagements.confirmCancel') || 'Confirm Cancellation',
            message: t('myEngagements.cancelMessage') || 'Are you sure you want to cancel this engagement?',
            confirmLabel: t('myEngagements.confirmYes') || 'Yes, Cancel',
            cancelLabel: t('common.cancel') || 'Cancel',
            tone: 'danger'
        });

        if (!confirmed) {
            return;
        }

        const auth = ensureAuthenticated();
        if (!auth.allowed) {
            showToast({ message: t('myEngagements.loginRequired'), type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;

        try {
            const response = await fetch(getApiUrl(`engagements/${engagementId}/cancel`), {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await readJsonSafe<any>(response);
                throw new Error(getApiErrorMessage(response, t('myEngagements.cancelFailed') || 'Failed to cancel engagement', errorData));
            }

            showToast({ message: t('myEngagements.cancelSuccess'), type: 'success' });
            
            // Refresh engagements list
            fetchEngagements();
        } catch (error) {
            console.error('Error cancelling engagement:', error);
            showToast({ message: error instanceof Error ? error.message : t('myEngagements.cancelFailed'), type: 'error' });
        }
    };

    const toggleExpand = (id: string) => {
        const newExpanded = new Set(expandedCards);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedCards(newExpanded);
    };

    const getStatusBadge = (engagement: Engagement) => {
        switch (engagement.status) {
            case 'pending':
                return <span className={styles.statusBadge + ' ' + styles.statusPending}>⏳ {t('myEngagements.status.pending')}</span>;
            case 'confirmed':
                return <span className={styles.statusBadge + ' ' + styles.statusConfirmed}>✓ {t('myEngagements.status.confirmed')}</span>;
            case 'completed':
                if (engagement.completedBy === 'both') {
                    return <span className={styles.statusBadge + ' ' + styles.statusCompleted}>✓ {t('myEngagements.status.completedBoth')}</span>;
                } else {
                    return <span className={styles.statusBadge + ' ' + styles.statusCompletedPartial}>⏳ {t('myEngagements.status.completedPartial')}</span>;
                }
            case 'cancelled':
                return <span className={styles.statusBadge + ' ' + styles.statusCancelled}>✗ {t('myEngagements.status.cancelled')}</span>;
            default:
                return null;
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            weekday: 'long',
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const isPastDate = (dateString: string) => {
        const date = new Date(dateString);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const activeEngagements = engagements.filter(e => 
        e.status === 'confirmed' || e.status === 'pending'
    );
    
    // Engagements completed and ready to rate (not yet rated)
    const pendingRatingEngagements = engagements.filter(e => 
        e.status === 'completed' && 
        e.completedBy === 'both' && 
        !ratedEngagements.has(e._id) &&
        canRateEngagement(e)
    );
    
    const completedEngagements = engagements.filter(e => 
        (e.status === 'completed' || e.status === 'cancelled') &&
        !pendingRatingEngagements.includes(e) // Don't show in both sections
    );

    if (loading) {
        return (
            <div className={styles.myEngagementsPage}>
                <div className={styles.loadingSpinner}>{t('myEngagements.loading')}</div>
            </div>
        );
    }

    return (
        <div className={styles.myEngagementsPage}>
            <div className={styles.listPageHeader}>
                <button onClick={() => navigate('/dashboard')} className={headerStyles.backButton}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>

                </button>
                <h1 className={styles.headerTitle}>{t('myEngagements.title')}</h1>
                <div></div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}

            {/* Active Engagements Section */}
            <section className={styles.engagementsSection}>
                <h2 className={styles.sectionTitle}>
                    {t('myEngagements.activeEngagements')} ({activeEngagements.length})
                </h2>
                
                {activeEngagements.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>{t('myEngagements.noActiveEngagements')}</p>
                        <button 
                            onClick={() => navigate(isEmployer ? '/search-staff' : '/search-job')}
                            className={styles.primaryButton}
                        >
                            {isEmployer ? t('myEngagements.findStaff') : t('myEngagements.findJobs')}
                        </button>
                    </div>
                ) : (
                    <div className={styles.engagementsGrid}>
                        {activeEngagements.map((engagement) => (
                            <div key={engagement._id} className={styles.engagementCard}>
                                <div className={styles.cardHeader}>
                                    <div>
                                        <h3>{t(`roles.${engagement.jobRole.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`)}</h3>
                                        <p className={styles.facilityName}>{engagement.facilityName || engagement.place}</p>
                                    </div>
                                    {getStatusBadge(engagement)}
                                </div>

                                <div className={styles.cardDetails}>
                                    <div className={styles.detailRow}>
                                        <span className={styles.icon}>📅</span>
                                        <span>{formatDate(engagement.date)}</span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <span className={styles.icon}>⏰</span>
                                        <span>{engagement.startTime} - {engagement.endTime}</span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <span className={styles.icon}>📍</span>
                                        <span>{engagement.place}</span>
                                    </div>
                                    {engagement.payRate && (
                                        <div className={styles.detailRow}>
                                            <span className={styles.icon}>💰</span>
                                            <span>{engagement.payRate}</span>
                                        </div>
                                    )}
                                </div>

                                <div className={styles.otherPartyInfo}>
                                    <h4>{engagement.otherParty.userType === 'employer' ? t('myEngagements.employer') : t('myEngagements.staffMember')}</h4>
                                    <p><strong>{engagement.otherParty.businessName || engagement.otherParty.fullName}</strong></p>
                                    <p>📧 {engagement.otherParty.email}</p>
                                    {engagement.otherParty.phone && (
                                        <p>📱 {engagement.otherParty.phone}</p>
                                    )}
                                </div>

                                <div className={styles.toggleDetails}>
                                    <button
                                        onClick={() => toggleExpand(engagement._id)}
                                        className={styles.secondaryButton}
                                    >
                                        {expandedCards.has(engagement._id) ? t('myEngagements.showLess') : t('myEngagements.showMore')}
                                    </button>
                                </div>

                                {expandedCards.has(engagement._id) && (
                                    <div className={styles.expandedDetails}>
                                        {engagement.description && (
                                            <div>
                                                <strong>{t('myEngagements.description')}</strong>
                                                <p>{engagement.description}</p>
                                            </div>
                                        )}
                                        {engagement.notes && (
                                            <div>
                                                <strong>{t('myEngagements.notes')}</strong>
                                                <p>{engagement.notes}</p>
                                            </div>
                                        )}
                                        <div>
                                            <strong>{t('myEngagements.startedBy')}</strong>
                                            <p>{engagement.startedBy}</p>
                                        </div>
                                    </div>
                                )}
                                {(engagement.status === 'pending' || engagement.status === 'confirmed') && (
                                    <div className={styles.cardActions}>
                                        {engagement.status === 'pending' && (
                                            <>
                                                <button
                                                    onClick={() => handleConfirmEngagement(engagement._id)}
                                                    className={styles.confirmButton}
                                                >
                                                    ✓ {t('common.confirm')}
                                                </button>
                                                <button
                                                    onClick={() => handleCancelEngagement(engagement._id)}
                                                    className={styles.cancelButton}
                                                >
                                                    ✗ {t('myEngagements.cancel')}
                                                </button>
                                            </>
                                        )}

                                        {engagement.status === 'confirmed' && (
                                            <>
                                                {isPastDate(engagement.date) && (
                                                    <button
                                                        onClick={() => handleMarkComplete(engagement._id)}
                                                        className={styles.completeButton}
                                                    >
                                                        ✓ {t('myEngagements.markComplete')}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleCancelEngagement(engagement._id)}
                                                    className={styles.cancelButton}
                                                >
                                                    ✗ {t('myEngagements.cancel')}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Pending Ratings Section - Completed jobs waiting to be rated */}
            {pendingRatingEngagements.length > 0 && (
                <section className={styles.engagementsSection + ' ' + styles.pendingRatingsSection}>
                    <h2 className={styles.sectionTitle + ' ' + styles.highlight}>
                        ⭐ {t('myEngagements.pendingRatings')} ({pendingRatingEngagements.length})
                    </h2>
                    <p className={styles.sectionSubtitle}>{t('myEngagements.pendingRatingsSubtitle')}</p>
                    
                    <div className={styles.engagementsGrid}>
                        {pendingRatingEngagements.map((engagement) => (
                            <div key={engagement._id} className={styles.engagementCard + ' ' + styles.pendingRating}>
                                <div className={styles.cardHeader}>
                                    <div>
                                        <h3>{engagement.jobRole}</h3>
                                        <p className={styles.facilityName}>{engagement.facilityName || engagement.place}</p>
                                    </div>
                                    <span className={styles.statusBadge + ' ' + styles.statusRatePending}>⭐ {t('myEngagements.readyToRate')}</span>
                                </div>

                                <div className={styles.cardDetails}>
                                    <div className={styles.detailRow}>
                                        <span className={styles.icon}>📅</span>
                                        <span>{formatDate(engagement.date)}</span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <span className={styles.icon}>⏰</span>
                                        <span>{engagement.startTime} - {engagement.endTime}</span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <span className={styles.icon}>📍</span>
                                        <span>{engagement.place}</span>
                                    </div>
                                </div>

                                <div className={styles.otherPartyInfo}>
                                    <p><strong>{engagement.otherParty.businessName || engagement.otherParty.fullName}</strong></p>
                                    {engagement.completedDate && (
                                        <p className={styles.completedDate}>
                                            {t('myEngagements.completed')} {new Date(engagement.completedDate).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>

                                <div className={styles.cardActions}>
                                    <button
                                        onClick={() => handleOpenRating(engagement)}
                                        className={styles.rateButton + ' ' + styles.primary}
                                    >
                                        ⭐ {t('myEngagements.rateNow')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Completed Engagements Section */}
            <section className={styles.engagementsSection}>
                <h2 className={styles.sectionTitle}>
                    {t('myEngagements.completedEngagements')} ({completedEngagements.length})
                </h2>
                
                {completedEngagements.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>{t('myEngagements.noCompletedEngagements')}</p>
                    </div>
                ) : (
                    <div className={styles.engagementsGrid}>
                        {completedEngagements.map((engagement) => (
                            <div key={engagement._id} className={styles.engagementCard + ' ' + styles.completed}>
                                <div className={styles.cardHeader}>
                                    <div>
                                        <h3>{engagement.jobRole}</h3>
                                        <p className={styles.facilityName}>{engagement.facilityName || engagement.place}</p>
                                    </div>
                                    {getStatusBadge(engagement)}
                                </div>

                                <div className={styles.cardDetails}>
                                    <div className={styles.detailRow}>
                                        <span className={styles.icon}>📅</span>
                                        <span>{formatDate(engagement.date)}</span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <span className={styles.icon}>⏰</span>
                                        <span>{engagement.startTime} - {engagement.endTime}</span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <span className={styles.icon}>📍</span>
                                        <span>{engagement.place}</span>
                                    </div>
                                </div>

                                <div className={styles.otherPartyInfo}>
                                    <p><strong>{engagement.otherParty.businessName || engagement.otherParty.fullName}</strong></p>
                                    {engagement.completedDate && (
                                        <p className={styles.completedDate}>
                                            {t('myEngagements.completed')} {new Date(engagement.completedDate).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>

                                {expandedCards.has(engagement._id) && (
                                    <div className={styles.expandedDetails}>
                                        {engagement.description && (
                                            <div>
                                                <strong>{t('myEngagements.description')}</strong>
                                                <p>{engagement.description}</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className={styles.toggleDetails}>
                                    <button
                                        onClick={() => toggleExpand(engagement._id)}
                                        className={styles.secondaryButton}
                                    >
                                        {expandedCards.has(engagement._id) ? t('myEngagements.showLess') : t('myEngagements.details')}
                                    </button>
                                </div>

                                <div className={styles.cardActions}>
                                    {engagement.status === 'completed' && engagement.completedBy !== 'both' && (
                                        <button
                                            className={styles.waitingButton}
                                            disabled
                                        >
                                            ⏳ {t('myEngagements.waitingForConfirmation')}
                                        </button>
                                    )}
                                    
                                    {canRateEngagement(engagement) && (
                                        <button
                                            onClick={() => handleOpenRating(engagement)}
                                            className={styles.rateButton}
                                        >
                                            ⭐ {t('myEngagements.rateEngagement')}
                                        </button>
                                    )}
                                    
                                    {engagement.status === 'completed' && engagement.completedBy === 'both' && ratedEngagements.has(engagement._id) && (
                                        <div className={styles.ratedBadge}>
                                            ✓ {t('myEngagements.rated')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Rating Modal */}
            {selectedEngagement && (
                <RatingModal
                    isOpen={ratingModalOpen}
                    onClose={() => {
                        setRatingModalOpen(false);
                        setSelectedEngagement(null);
                    }}
                    engagement={selectedEngagement}
                    userType={isEmployer ? 'employer' : 'staff'}
                    onRatingSubmitted={handleRatingSubmitted}
                />
            )}

        </div>
    );
};

export default MyEngagements;
